/**
 * Google Pay Service (Native, framework-agnostic)
 *
 * Tokenizes a native Google Pay sheet via BasisTheory and returns a
 * card-shaped token. No React, no checkout session, no Tagada client —
 * pure browser primitive suitable for Server-to-Server (S2S) partner
 * integrations.
 *
 * Use this when `paymentSetupConfig.google_pay.provider === 'google_pay'`
 * (i.e. native Google Pay routed through Tagada/BasisTheory, not Stripe).
 * For Google Pay routed through Stripe (`provider === 'stripe'`), use
 * `startStripeExpressCheckout` instead.
 *
 * Mirrors `@tagadapay/plugin-sdk/v2/standalone` google-pay-service.ts so
 * partner code can rely on `@tagadapay/core-js` as the canonical home.
 *
 * Must be invoked from a user gesture (click handler).
 *
 * @example
 * ```ts
 * import { isGooglePayAvailable, startGooglePaySession } from '@tagadapay/core-js';
 *
 * if (await isGooglePayAvailable()) {
 *   button.addEventListener('click', () => {
 *     startGooglePaySession(
 *       {
 *         basisTheoryApiKey: 'pk_***',
 *         basisTheoryTenantId: 'tnt_***',
 *         merchantId: '01234567890123456789',
 *         merchantName: 'Acme',
 *       },
 *       { currency: 'USD', totalAmountMinor: 1999 },
 *       {
 *         onSuccess: async (token, contacts) => {
 *           // POST token.id + contacts to your server
 *         },
 *       },
 *     );
 *   });
 * }
 * ```
 */

const GOOGLE_PAY_SDK_URL = 'https://pay.google.com/gp/p/js/pay.js';

let sdkLoadPromise: Promise<void> | null = null;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GooglePayServiceConfig {
  /** BasisTheory public API key (used for tokenization POST) */
  basisTheoryApiKey: string;
  /** BasisTheory tenant ID (used as gatewayMerchantId for Google Pay) */
  basisTheoryTenantId: string;
  /** Google Pay merchant ID (from Google Pay Business Console) */
  merchantId: string;
  /** Merchant display name shown in the Google Pay sheet */
  merchantName?: string;
  /** Country code for the merchant (e.g. 'US') */
  countryCode?: string;
  /** Whether to use the TEST environment (default: false → PRODUCTION) */
  sandboxed?: boolean;
}

export interface GooglePayPaymentRequest {
  /** ISO 4217 currency code (e.g. 'USD') */
  currency: string;
  /** Total amount in **minor units** (cents) */
  totalAmountMinor: number;
  /** Total price status: 'FINAL' | 'ESTIMATED' */
  totalPriceStatus?: 'FINAL' | 'ESTIMATED';
  /** Optional display items */
  displayItems?: Array<{
    label: string;
    type: 'LINE_ITEM' | 'SUBTOTAL' | 'TAX' | 'DISCOUNT' | 'SHIPPING_OPTION';
    price: string;
    status?: 'FINAL' | 'PENDING';
  }>;
}

export interface GooglePayTokenResult {
  /** BasisTheory token ID — pass to /payment-instruments/create-from-token */
  id: string;
  type: string;
  card: {
    bin: string;
    last4: string;
    expiration_month: number;
    expiration_year: number;
    brand: string;
  };
  details?: {
    auth_method?: string;
    [key: string]: unknown;
  };
}

export interface GooglePayAddress {
  name?: string;
  phoneNumber?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  locality?: string;
  administrativeArea?: string;
  postalCode?: string;
  countryCode?: string;
  sortingCode?: string;
}

export interface GooglePayCallbacks {
  onSuccess: (token: GooglePayTokenResult, contacts: {
    shipping?: GooglePayAddress;
    billing?: GooglePayAddress;
    email?: string;
  }) => void | Promise<void>;
  onError?: (error: string) => void;
  onCancel?: () => void;
}

// ---------------------------------------------------------------------------
// SDK Loading
// ---------------------------------------------------------------------------

function loadGooglePaySdk(): Promise<void> {
  if (sdkLoadPromise) return sdkLoadPromise;

  if (typeof window === 'undefined') {
    return Promise.reject(new Error('[GooglePayService] No window — not in browser'));
  }

  if ((window as any).google?.payments?.api?.PaymentsClient) {
    sdkLoadPromise = Promise.resolve();
    return sdkLoadPromise;
  }

  const existing = document.querySelector(`script[src="${GOOGLE_PAY_SDK_URL}"]`);
  if (existing) {
    sdkLoadPromise = new Promise<void>((resolve) => {
      if ((window as any).google?.payments?.api?.PaymentsClient) return resolve();
      existing.addEventListener('load', () => resolve());
      const check = setInterval(() => {
        if ((window as any).google?.payments?.api?.PaymentsClient) {
          clearInterval(check);
          resolve();
        }
      }, 100);
      setTimeout(() => { clearInterval(check); resolve(); }, 5000);
    });
    return sdkLoadPromise;
  }

  sdkLoadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GOOGLE_PAY_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      sdkLoadPromise = null;
      reject(new Error('[GooglePayService] Failed to load Google Pay SDK'));
    };
    document.head.appendChild(script);
  });

  return sdkLoadPromise;
}

// ---------------------------------------------------------------------------
// Availability
// ---------------------------------------------------------------------------

const BASE_CARD_PAYMENT_METHOD = {
  type: 'CARD',
  parameters: {
    allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
    allowedCardNetworks: ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER', 'JCB'],
  },
};

/**
 * Loads the Google Pay SDK and probes browser/device capability.
 * Returns `true` if Google Pay is available, `false` otherwise.
 */
export async function isGooglePayAvailable(sandboxed = false): Promise<boolean> {
  try {
    await loadGooglePaySdk();

    const PaymentsClient = (window as any).google?.payments?.api?.PaymentsClient;
    if (!PaymentsClient) return false;

    const client = new PaymentsClient({
      environment: sandboxed ? 'TEST' : 'PRODUCTION',
    });

    const result = await client.isReadyToPay({
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [BASE_CARD_PAYMENT_METHOD],
    });

    return result.result === true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// BasisTheory API Helpers
// ---------------------------------------------------------------------------

async function tokenizeGooglePayPayment(
  basisTheoryApiKey: string,
  paymentToken: string,
): Promise<GooglePayTokenResult> {
  let parsedToken: any;
  try {
    parsedToken = typeof paymentToken === 'string' ? JSON.parse(paymentToken) : paymentToken;
  } catch {
    parsedToken = paymentToken;
  }

  const response = await fetch('https://api.basistheory.com/google-pay', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'BT-API-KEY': basisTheoryApiKey,
    },
    body: JSON.stringify({
      google_payment_data: parsedToken,
    }),
  });

  if (!response.ok) {
    throw new Error(`[GooglePayService] Tokenization failed: HTTP ${response.status}`);
  }

  const result = await response.json();
  return result.google_pay;
}

// ---------------------------------------------------------------------------
// Currency Helpers
// ---------------------------------------------------------------------------

function minorToDecimal(amountMinor: number, currency: string): string {
  const zeroDecimal = ['BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA',
    'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'];
  if (zeroDecimal.includes(currency.toUpperCase())) {
    return String(amountMinor);
  }
  return (amountMinor / 100).toFixed(2);
}

// ---------------------------------------------------------------------------
// Address Helpers
// ---------------------------------------------------------------------------

function extractAddress(data: any): GooglePayAddress | undefined {
  if (!data) return undefined;
  return {
    name: data.name,
    phoneNumber: data.phoneNumber,
    address1: data.address1,
    address2: data.address2,
    address3: data.address3,
    locality: data.locality,
    administrativeArea: data.administrativeArea,
    postalCode: data.postalCode,
    countryCode: data.countryCode,
    sortingCode: data.sortingCode,
  };
}

// ---------------------------------------------------------------------------
// Session Creation
// ---------------------------------------------------------------------------

/**
 * Opens the native Google Pay sheet, drives tokenization through
 * BasisTheory, and surfaces the resulting token via `callbacks.onSuccess`.
 * Must be called from a user gesture.
 */
export function startGooglePaySession(
  config: GooglePayServiceConfig,
  request: GooglePayPaymentRequest,
  callbacks: GooglePayCallbacks,
): void {
  const PaymentsClient = (window as any).google?.payments?.api?.PaymentsClient;
  if (!PaymentsClient) {
    callbacks.onError?.('Google Pay is not available on this device');
    return;
  }

  const countryCode = config.countryCode || 'US';
  const merchantName = config.merchantName || 'Store';
  const totalStr = minorToDecimal(request.totalAmountMinor, request.currency);

  const client = new PaymentsClient({
    environment: config.sandboxed ? 'TEST' : 'PRODUCTION',
  });

  const paymentDataRequest: any = {
    apiVersion: 2,
    apiVersionMinor: 0,
    allowedPaymentMethods: [
      {
        ...BASE_CARD_PAYMENT_METHOD,
        tokenizationSpecification: {
          type: 'PAYMENT_GATEWAY',
          parameters: {
            gateway: 'basistheory',
            gatewayMerchantId: config.basisTheoryTenantId,
          },
        },
      },
    ],
    merchantInfo: {
      merchantId: config.sandboxed ? '12345678901234567890' : config.merchantId,
      merchantName,
    },
    transactionInfo: {
      totalPriceStatus: request.totalPriceStatus || 'FINAL',
      totalPrice: totalStr,
      currencyCode: request.currency,
      countryCode,
    },
    emailRequired: true,
    shippingAddressRequired: true,
    shippingAddressParameters: {
      phoneNumberRequired: true,
    },
  };

  if (request.displayItems) {
    paymentDataRequest.transactionInfo.displayItems = request.displayItems;
  }

  try {
    client
      .loadPaymentData(paymentDataRequest)
      .then(async (paymentData: any) => {
        try {
          const paymentMethodData = paymentData.paymentMethodData;
          const tokenString = paymentMethodData?.tokenizationData?.token;

          if (!tokenString) {
            throw new Error('No payment token received from Google Pay');
          }

          const token = await tokenizeGooglePayPayment(
            config.basisTheoryApiKey,
            tokenString,
          );

          if (paymentMethodData?.info) {
            token.details = {
              auth_method: paymentMethodData.info.cardNetwork,
              ...token.details,
            };
          }

          const shippingAddress = extractAddress(paymentData.shippingAddress);
          const billingAddress = extractAddress(paymentMethodData?.info?.billingAddress);
          const email = paymentData.email;

          await callbacks.onSuccess(token, {
            shipping: shippingAddress,
            billing: billingAddress,
            email,
          });
        } catch (err) {
          console.error('[GooglePayService] Payment processing failed:', err);
          callbacks.onError?.('Payment failed');
        }
      })
      .catch((err: any) => {
        if (err.statusCode === 'CANCELED') {
          callbacks.onCancel?.();
        } else {
          console.error('[GooglePayService] Google Pay error:', err);
          callbacks.onError?.(err.statusMessage || 'Google Pay failed');
        }
      });
  } catch (err) {
    console.error('[GooglePayService] Failed to start Google Pay session:', err);
    callbacks.onError?.('Failed to start Google Pay');
  }
}
